import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Plus, MagnifyingGlass, ArrowLeft, Trash, UserPlus, CurrencyDollar,
  TrendUp, TrendDown, CalendarBlank, ChartLine, Wallet, Clock, Star,
  User, Buildings, Envelope, Phone, MapPin, Globe, IdentificationCard,
  PencilSimple, Eye, FunnelSimple, ArrowsDownUp
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const PIE_COLORS = ['#0F6CBD', '#107C41', '#D83B01', '#FFB900', '#D13438', '#5C2D91', '#0078D4', '#00B294'];
const STATUS_STYLES = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-slate-100 text-slate-600 border-slate-200',
  lapsed: 'bg-red-50 text-red-700 border-red-200',
  'at-risk': 'bg-amber-50 text-amber-700 border-amber-200',
};

const fmtCurrency = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function DonorsHub() {
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [showForm, setShowForm] = useState(false);
  const [editDonor, setEditDonor] = useState(null);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [donorStats, setDonorStats] = useState(null);
  const [showDonation, setShowDonation] = useState(false);
  const [showPairStudent, setShowPairStudent] = useState(false);
  const [students, setStudents] = useState([]);
  const [categories, setCategories] = useState([]);

  const fetchDonors = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterType) params.set('type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      if (filterCategory) params.set('category', filterCategory);
      const { data } = await api.get(`/donors?${params}`);
      setDonors(data);
    } catch { toast.error('Failed to load donors'); }
    setLoading(false);
  }, [search, filterType, filterStatus, filterCategory]);

  useEffect(() => { fetchDonors(); }, [fetchDonors]);
  useEffect(() => {
    api.get('/recipients?type=student&status=active').then(r => setStudents(r.data)).catch(() => {});
    api.get('/admin/categories?type=donor').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const selectDonor = async (id) => {
    try {
      const [donor, stats] = await Promise.all([
        api.get(`/donors/${id}`),
        api.get(`/donors/${id}/stats`)
      ]);
      setSelectedDonor(donor.data);
      setDonorStats(stats.data);
    } catch { toast.error('Failed to load donor'); }
  };

  const sorted = [...donors].sort((a, b) => {
    const av = a[sortField], bv = b[sortField];
    if (sortField === 'total_donated' || sortField === 'donation_count') return sortDir === 'asc' ? (av||0)-(bv||0) : (bv||0)-(av||0);
    return sortDir === 'asc' ? String(av||'').localeCompare(String(bv||'')) : String(bv||'').localeCompare(String(av||''));
  });
  const toggleSort = (field) => { setSortField(field); setSortDir(sortField === field && sortDir === 'desc' ? 'asc' : 'desc'); };

  /* ─── DONOR CREATE/EDIT FORM ─── */
  const DonorForm = () => {
    const [form, setForm] = useState(editDonor || {
      name: '', type: 'individual', email: '', phone: '', address: '', category: '',
      notes: '', company: '', website: '', tax_id: '', preferred_contact: 'email', status: 'active'
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm({ ...form, [k]: v });
    const handleSave = async () => {
      if (!form.name) { toast.error('Name is required'); return; }
      setSaving(true);
      try {
        if (editDonor?.id) {
          await api.put(`/donors/${editDonor.id}`, form);
          toast.success('Donor updated');
          if (selectedDonor) selectDonor(editDonor.id);
        } else {
          await api.post('/donors', form);
          toast.success('Donor created');
        }
        setShowForm(false); setEditDonor(null); fetchDonors();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setSaving(false);
    };
    return (
      <Dialog open onOpenChange={() => { setShowForm(false); setEditDonor(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="donor-form-dialog">
          <DialogHeader><DialogTitle className="font-['Chivo'] text-lg">{editDonor?.id ? 'Edit Donor' : 'New Donor'}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Full Name *</Label>
                <Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="donor-name-input" className="rounded-sm" placeholder="John Smith" /></div>
              <div><Label className="data-label">Donor Type</Label>
                <Select value={form.type} onValueChange={v => set('type', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="donor-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="individual">Individual</SelectItem><SelectItem value="organization">Organization</SelectItem><SelectItem value="church">Church/Religious</SelectItem><SelectItem value="foundation">Foundation</SelectItem><SelectItem value="corporate">Corporate</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Company / Organization</Label>
                <Input value={form.company} onChange={e => set('company', e.target.value)} className="rounded-sm" placeholder="Acme Corp" /></div>
              <div><Label className="data-label">Status</Label>
                <Select value={form.status || 'active'} onValueChange={v => set('status', v)}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="lapsed">Lapsed</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="data-label">Email</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} className="rounded-sm" data-testid="donor-email-input" /></div>
              <div><Label className="data-label">Phone</Label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)} className="rounded-sm" /></div>
              <div><Label className="data-label">Preferred Contact</Label>
                <Select value={form.preferred_contact || 'email'} onValueChange={v => set('preferred_contact', v)}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="phone">Phone</SelectItem><SelectItem value="mail">Mail</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Category</Label>
                <Select value={form.category || ''} onValueChange={v => set('category', v)}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="data-label">Tax ID / EIN</Label>
                <Input value={form.tax_id} onChange={e => set('tax_id', e.target.value)} className="rounded-sm" placeholder="XX-XXXXXXX" /></div>
            </div>
            <div><Label className="data-label">Website</Label>
              <Input value={form.website} onChange={e => set('website', e.target.value)} className="rounded-sm" placeholder="https://..." /></div>
            <div><Label className="data-label">Address</Label>
              <Textarea value={form.address} onChange={e => set('address', e.target.value)} className="rounded-sm" rows={2} /></div>
            <div><Label className="data-label">Notes</Label>
              <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="rounded-sm" rows={2} /></div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm" data-testid="donor-save-btn">
              {saving ? 'Saving...' : editDonor?.id ? 'Update Donor' : 'Create Donor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  /* ─── RECORD DONATION ─── */
  const DonationForm = () => {
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [allocations, setAllocations] = useState([]);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
      const paired = selectedDonor?.paired_student_details || [];
      setAllocations([
        { type: 'central_fund', recipient_id: null, amount: 0, label: 'Central Fund Pool' },
        ...paired.map(s => ({ type: 'student', recipient_id: s.id, amount: 0, label: s.name }))
      ]);
    }, []);
    const totalAllocated = allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    const remaining = (parseFloat(amount) || 0) - totalAllocated;
    const handleAllocChange = (idx, val) => { const next = [...allocations]; next[idx].amount = parseFloat(val) || 0; setAllocations(next); };
    const handleSave = async () => {
      if (!amount || parseFloat(amount) <= 0) { toast.error('Amount required'); return; }
      if (Math.abs(remaining) > 0.01) { toast.error(`Allocate full amount. Remaining: $${remaining.toFixed(2)}`); return; }
      setSaving(true);
      try {
        await api.post('/donors/donations', {
          donor_id: selectedDonor.id, amount: parseFloat(amount), date, reference, notes,
          allocations: allocations.filter(a => a.amount > 0).map(a => ({ type: a.type, recipient_id: a.recipient_id, amount: a.amount }))
        });
        toast.success('Donation recorded'); setShowDonation(false); selectDonor(selectedDonor.id);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setSaving(false);
    };
    return (
      <Dialog open onOpenChange={() => setShowDonation(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="donation-form-dialog">
          <DialogHeader><DialogTitle className="font-['Chivo']">Record Donation - {selectedDonor?.name}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Amount (USD) *</Label>
                <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="donation-amount-input" className="rounded-sm" placeholder="0.00" /></div>
              <div><Label className="data-label">Date *</Label>
                <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="rounded-sm" data-testid="donation-date-input" /></div>
            </div>
            <div><Label className="data-label">Reference</Label>
              <Input value={reference} onChange={e => setReference(e.target.value)} className="rounded-sm" placeholder="Check #, wire ref, etc." /></div>
            <div><Label className="data-label">Notes</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="rounded-sm" rows={2} /></div>
            <div className="border-t pt-4">
              <div className="flex justify-between mb-3">
                <span className="data-label">Fund Allocation</span>
                <span className={`text-sm font-mono font-medium ${Math.abs(remaining) < 0.01 ? 'text-emerald-600' : 'text-red-600'}`}>
                  Remaining: ${remaining.toFixed(2)}
                </span>
              </div>
              {/* Quick allocation buttons */}
              {parseFloat(amount) > 0 && allocations.filter(a => a.type === 'student').length > 0 && (
                <div className="flex gap-2 mb-3 flex-wrap">
                  <Button type="button" variant="outline" size="sm" className="text-xs rounded-sm" data-testid="split-all-equal-btn"
                    onClick={() => {
                      const studentAllocs = allocations.filter(a => a.type === 'student');
                      const perStudent = Math.floor((parseFloat(amount) / studentAllocs.length) * 100) / 100;
                      const lastAdj = parseFloat(amount) - perStudent * (studentAllocs.length - 1);
                      const next = allocations.map((a, i) => {
                        if (a.type === 'central_fund') return { ...a, amount: 0 };
                        const studentIdx = allocations.filter((x, j) => j < i && x.type === 'student').length;
                        return { ...a, amount: studentIdx === studentAllocs.length - 1 ? Math.round(lastAdj * 100) / 100 : perStudent };
                      });
                      setAllocations(next);
                    }}>Split All Equally to Students</Button>
                  <Button type="button" variant="outline" size="sm" className="text-xs rounded-sm" data-testid="split-remainder-equal-btn"
                    onClick={() => {
                      const centralAmount = allocations.find(a => a.type === 'central_fund')?.amount || 0;
                      const studentAllocs = allocations.filter(a => a.type === 'student');
                      if (studentAllocs.length === 0) return;
                      const remainderForStudents = parseFloat(amount) - centralAmount;
                      if (remainderForStudents <= 0) return;
                      const perStudent = Math.floor((remainderForStudents / studentAllocs.length) * 100) / 100;
                      const lastAdj = remainderForStudents - perStudent * (studentAllocs.length - 1);
                      let studentIdx = 0;
                      const next = allocations.map(a => {
                        if (a.type === 'central_fund') return a;
                        const val = studentIdx === studentAllocs.length - 1 ? Math.round(lastAdj * 100) / 100 : perStudent;
                        studentIdx++;
                        return { ...a, amount: val };
                      });
                      setAllocations(next);
                    }}>Split Remainder Equally</Button>
                  <Button type="button" variant="outline" size="sm" className="text-xs rounded-sm"
                    onClick={() => {
                      const next = allocations.map(a => a.type === 'central_fund' ? { ...a, amount: parseFloat(amount) || 0 } : { ...a, amount: 0 });
                      setAllocations(next);
                    }}>All to Central Fund</Button>
                </div>
              )}
              {allocations.map((a, i) => (
                <div key={i} className="flex items-center gap-3 mb-2 p-2 bg-slate-50 rounded-sm">
                  <span className="text-sm text-slate-700 flex-1 truncate font-medium">{a.label}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-400">$</span>
                    <Input type="number" step="0.01" value={a.amount || ''} onChange={e => handleAllocChange(i, e.target.value)}
                      className="rounded-sm w-28 text-right" placeholder="0.00" data-testid={`alloc-${i}-amount`} />
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={handleSave} disabled={saving || Math.abs(remaining) > 0.01} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm" data-testid="donation-save-btn">
              {saving ? 'Recording...' : 'Record Donation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  /* ─── PAIR STUDENT ─── */
  const PairStudentDialog = () => {
    const [studentId, setStudentId] = useState('');
    const paired = selectedDonor?.paired_students || [];
    const available = students.filter(s => !paired.includes(s.id));
    const handlePair = async () => {
      if (!studentId) return;
      try {
        await api.post(`/donors/${selectedDonor.id}/pair-student`, { student_id: studentId });
        toast.success('Student paired'); setShowPairStudent(false); selectDonor(selectedDonor.id);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    return (
      <Dialog open onOpenChange={() => setShowPairStudent(false)}>
        <DialogContent className="max-w-sm" data-testid="pair-student-dialog">
          <DialogHeader><DialogTitle className="font-['Chivo']">Pair Student to {selectedDonor?.name}</DialogTitle></DialogHeader>
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger className="rounded-sm" data-testid="pair-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
            <SelectContent>{available.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.school_name || 'No school'})</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={handlePair} disabled={!studentId} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm" data-testid="pair-student-btn">Pair Student</Button>
        </DialogContent>
      </Dialog>
    );
  };

  /* ═══════════════════ DETAIL VIEW ═══════════════════ */
  if (selectedDonor) {
    const s = donorStats || {};
    return (
      <div className="space-y-5" data-testid="donor-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedDonor(null); setDonorStats(null); }} data-testid="back-to-donors-btn">
              <ArrowLeft size={18} />
            </Button>
            <div className="w-10 h-10 bg-[#0F6CBD] rounded-sm flex items-center justify-center text-white font-bold font-['Chivo'] text-lg">
              {selectedDonor.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 font-['Chivo'] flex items-center gap-2">
                {selectedDonor.name}
                <Badge className={`text-xs capitalize border ${STATUS_STYLES[selectedDonor.status || s.computed_status || 'active']}`}>
                  {selectedDonor.status || s.computed_status || 'active'}
                </Badge>
              </h2>
              <span className="text-sm text-slate-500 capitalize">{selectedDonor.type}{selectedDonor.company ? ` · ${selectedDonor.company}` : ''}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setShowDonation(true)} className="bg-[#107C41] hover:bg-[#0B5A2F] text-white rounded-sm text-xs" data-testid="record-donation-btn">
              <CurrencyDollar size={14} className="mr-1" /> Record Donation
            </Button>
            <Button size="sm" onClick={() => { setEditDonor(selectedDonor); setShowForm(true); }} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm text-xs" data-testid="edit-donor-btn">
              <PencilSimple size={14} className="mr-1" /> Edit
            </Button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: 'Lifetime Total', val: fmtCurrency(s.lifetime_total), icon: Wallet, color: '#0F6CBD' },
            { label: 'YTD Total', val: fmtCurrency(s.ytd_total), icon: CalendarBlank, color: '#107C41' },
            { label: 'Avg Donation', val: fmtCurrency(s.average_donation), icon: ChartLine, color: '#5C2D91' },
            { label: 'Donations', val: s.lifetime_count || 0, icon: Star, color: '#D83B01' },
            { label: 'Last Donation', val: s.last_donation_date || 'Never', icon: Clock, color: '#0078D4' },
            { label: 'Frequency', val: s.avg_frequency_days ? `${s.avg_frequency_days}d` : 'N/A', icon: ArrowsDownUp, color: '#FFB900' },
            { label: 'YoY Growth', val: s.yoy_growth !== null && s.yoy_growth !== undefined ? `${s.yoy_growth > 0 ? '+' : ''}${s.yoy_growth}%` : 'N/A',
              icon: s.yoy_growth > 0 ? TrendUp : TrendDown, color: s.yoy_growth > 0 ? '#107C41' : '#D13438' },
          ].map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-sm p-3" data-testid={`donor-kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold tracking-[0.06em] uppercase text-slate-400">{kpi.label}</span>
                  <Icon size={14} color={kpi.color} weight="duotone" />
                </div>
                <p className="text-base font-semibold text-slate-900 font-mono">{kpi.val}</p>
              </div>
            );
          })}
        </div>

        {/* Tabbed Content */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="flex border-b border-slate-200 bg-transparent p-0 h-auto" data-testid="donor-detail-tabs">
            {['Overview', 'Donations', 'Paired Students', 'Activity'].map(tab => (
              <TabsTrigger key={tab} value={tab.toLowerCase().replace(/\s+/g, '-')}
                className="px-4 py-2.5 text-sm font-medium text-slate-500 border-b-2 border-transparent rounded-none data-[state=active]:text-[#0F6CBD] data-[state=active]:border-[#0F6CBD] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                data-testid={`donor-tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}>{tab}</TabsTrigger>
            ))}
          </TabsList>

          {/* ── OVERVIEW TAB ── */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Donation Trend */}
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] mb-3">Donation Trend (12 Months)</h3>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={s.monthly_trend || []}>
                      <defs><linearGradient id="colorDon" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0F6CBD" stopOpacity={0.15}/><stop offset="95%" stopColor="#0F6CBD" stopOpacity={0}/></linearGradient></defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={v => `$${v}`} />
                      <RTooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Amount']} />
                      <Area type="monotone" dataKey="total" stroke="#0F6CBD" strokeWidth={2} fill="url(#colorDon)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Allocation Breakdown */}
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] mb-3">Allocation Split</h3>
                {s.allocation_summary && Object.keys(s.allocation_summary).length > 0 ? (
                  <>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={Object.entries(s.allocation_summary).map(([k, v]) => ({ name: k === 'central_fund' ? 'Central Fund' : 'Students', value: v.total }))}
                            dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3}>
                            {Object.keys(s.allocation_summary).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <RTooltip formatter={(v) => fmtCurrency(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2">
                      {Object.entries(s.allocation_summary).map(([k, v], i) => (
                        <div key={k} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i] }} />
                            <span className="text-slate-600">{k === 'central_fund' ? 'Central Fund' : 'Students'}</span>
                          </div>
                          <span className="font-mono font-medium text-slate-800">{fmtCurrency(v.total)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <p className="text-sm text-slate-400 mt-8 text-center">No allocation data</p>}
              </div>
            </div>

            {/* Donor Profile + Milestones row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Profile Card */}
              <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 font-['Chivo']">Contact Information</h3>
                {[
                  [Envelope, 'Email', selectedDonor.email],
                  [Phone, 'Phone', selectedDonor.phone],
                  [MapPin, 'Address', selectedDonor.address],
                  [Buildings, 'Company', selectedDonor.company],
                  [Globe, 'Website', selectedDonor.website],
                  [IdentificationCard, 'Tax ID', selectedDonor.tax_id],
                ].map(([Icon, label, val]) => val ? (
                  <div key={label} className="flex items-start gap-2">
                    <Icon size={14} className="text-slate-400 mt-0.5 shrink-0" />
                    <div><span className="text-[10px] uppercase tracking-wider text-slate-400 block">{label}</span>
                      <span className="text-sm text-slate-700">{val}</span></div>
                  </div>
                ) : null)}
                {![selectedDonor.email, selectedDonor.phone, selectedDonor.address, selectedDonor.company, selectedDonor.website, selectedDonor.tax_id].some(Boolean) && (
                  <p className="text-sm text-slate-400 italic">No contact information on file</p>
                )}
                {selectedDonor.notes && (
                  <div className="pt-2 border-t border-slate-100">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Notes</span>
                    <p className="text-sm text-slate-600 leading-relaxed">{selectedDonor.notes}</p>
                  </div>
                )}
              </div>

              {/* Key Milestones */}
              <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 font-['Chivo']">Key Milestones</h3>
                {[
                  ['First Donation', s.first_donation_date, s.first_donation_amount ? fmtCurrency(s.first_donation_amount) : null],
                  ['Last Donation', s.last_donation_date, s.last_donation_amount ? fmtCurrency(s.last_donation_amount) : null],
                  ['Largest Donation', s.largest_donation_date, s.largest_donation_amount ? fmtCurrency(s.largest_donation_amount) : null],
                  ['Days Since Last', null, s.days_since_last !== null ? `${s.days_since_last} days` : 'N/A'],
                  ['Donor Since', selectedDonor.created_at?.substring(0, 10), null],
                ].map(([label, date, val]) => (
                  <div key={label} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <span className="text-sm text-slate-600">{label}</span>
                    <div className="text-right">
                      {val && <span className="font-mono text-sm font-medium text-slate-800 block">{val}</span>}
                      {date && <span className="text-xs text-slate-400">{date}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Yearly Comparison */}
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] mb-3">Annual Totals</h3>
                {s.yearly_data?.length > 0 ? (
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={s.yearly_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={v => `$${v}`} />
                        <RTooltip formatter={(v) => [fmtCurrency(v), 'Total']} />
                        <Bar dataKey="total" fill="#0F6CBD" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : <p className="text-sm text-slate-400 mt-8 text-center">No annual data</p>}
              </div>
            </div>
          </TabsContent>

          {/* ── DONATIONS TAB ── */}
          <TabsContent value="donations" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">
                {selectedDonor.donations?.length || 0} donation(s) · Total: <span className="currency">{fmtCurrency(s.lifetime_total)}</span>
              </span>
              <Button size="sm" onClick={() => setShowDonation(true)} className="bg-[#107C41] hover:bg-[#0B5A2F] text-white rounded-sm text-xs" data-testid="record-donation-btn-tab">
                <CurrencyDollar size={14} className="mr-1" /> Record Donation
              </Button>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-200">
                  {['Date', 'Amount', 'Reference', 'Allocation', 'Notes', 'Recorded'].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(!selectedDonor.donations || selectedDonor.donations.length === 0) ? (
                    <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">No donations recorded yet</td></tr>
                  ) : selectedDonor.donations.map(d => (
                    <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`donation-row-${d.id}`}>
                      <td className="py-3 px-4 text-sm text-slate-700 whitespace-nowrap">{d.date}</td>
                      <td className="py-3 px-4 currency text-sm font-medium text-slate-900">{fmtCurrency(d.amount)}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{d.reference || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-0.5">
                          {d.allocations?.map((a, j) => (
                            <span key={j} className="text-xs text-slate-500">
                              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${a.type === 'central_fund' ? 'bg-[#0F6CBD]' : 'bg-[#107C41]'}`} />
                              {a.type === 'central_fund' ? 'Central Fund' : 'Student'}: <span className="font-mono">${a.amount?.toFixed(2)}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500 max-w-xs truncate">{d.notes || '-'}</td>
                      <td className="py-3 px-4 text-xs text-slate-400">{d.created_at?.substring(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ── PAIRED STUDENTS TAB ── */}
          <TabsContent value="paired-students" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">{selectedDonor.paired_student_details?.length || 0} student(s) paired</span>
              <Button size="sm" onClick={() => setShowPairStudent(true)} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-xs" data-testid="add-paired-student-btn">
                <UserPlus size={14} className="mr-1" /> Pair Student
              </Button>
            </div>
            {/* Student allocation pie chart */}
            {s.student_allocation_details?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] mb-2">Allocation Per Student</h3>
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={s.student_allocation_details.map(sa => ({ name: sa.recipient_name, value: sa.total }))}
                        dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} label={({ name, value }) => `${name}: $${value.toLocaleString()}`}>
                        {s.student_allocation_details.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <RTooltip formatter={(v) => fmtCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {/* Student cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(selectedDonor.paired_student_details || []).map(st => (
                <div key={st.id} className="bg-white border border-slate-200 rounded-sm p-4 flex items-start justify-between" data-testid={`paired-student-${st.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-slate-100 rounded-sm flex items-center justify-center text-slate-600 font-medium text-sm shrink-0">
                      {st.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{st.name}</p>
                      <p className="text-xs text-slate-500">{st.school_name || 'No school'} {st.grade ? `· Grade ${st.grade}` : ''}</p>
                      <Badge className={`text-[10px] mt-1 capitalize ${st.status === 'active' ? 'status-active' : 'status-dropout'}`}>{st.status}</Badge>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                        <div><span className="text-[10px] text-slate-400 uppercase block">From this donor</span>
                          <span className="currency text-sm font-semibold text-[#0F6CBD]">{fmtCurrency(st.received_from_donor)}</span></div>
                        <div><span className="text-[10px] text-slate-400 uppercase block">Total allocated</span>
                          <span className="currency text-sm font-medium text-slate-700">{fmtCurrency(st.total_allocated)}</span></div>
                        <div><span className="text-[10px] text-slate-400 uppercase block">Balance</span>
                          <span className="currency text-sm font-medium text-emerald-600">{fmtCurrency(st.wallet_balance)}</span></div>
                        <div><span className="text-[10px] text-slate-400 uppercase block">Requirement</span>
                          <span className="currency text-sm font-medium text-slate-600">{fmtCurrency(st.financial_requirement)}</span></div>
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 h-7 w-7 p-0 shrink-0" onClick={async () => {
                    await api.delete(`/donors/${selectedDonor.id}/unpair-student/${st.id}`);
                    toast.success('Student unpaired'); selectDonor(selectedDonor.id);
                  }}><Trash size={14} /></Button>
                </div>
              ))}
              {(!selectedDonor.paired_student_details || selectedDonor.paired_student_details.length === 0) && (
                <div className="col-span-2 bg-white border border-slate-200 border-dashed rounded-sm p-8 text-center">
                  <User size={28} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No students paired to this donor</p>
                  <Button size="sm" variant="ghost" className="mt-2 text-[#0F6CBD] text-xs" onClick={() => setShowPairStudent(true)}>Pair a student</Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── ACTIVITY TAB ── */}
          <TabsContent value="activity" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0"><tr className="bg-slate-50 border-b border-slate-200">
                  {['Timestamp', 'Action', 'Details'].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(!s.activity || s.activity.length === 0) ? (
                    <tr><td colSpan={3} className="py-8 text-center text-sm text-slate-400">No activity recorded</td></tr>
                  ) : s.activity.map((a, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 px-4 text-xs text-slate-500 whitespace-nowrap">{a.timestamp?.replace('T', ' ').substring(0, 19)}</td>
                      <td className="py-2.5 px-4"><Badge variant="outline" className="text-xs">{a.action?.replace(/_/g, ' ')}</Badge></td>
                      <td className="py-2.5 px-4 text-xs text-slate-600">{a.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        {showDonation && <DonationForm />}
        {showPairStudent && <PairStudentDialog />}
        {showForm && <DonorForm />}
      </div>
    );
  }

  /* ═══════════════════ LIST VIEW ═══════════════════ */
  return (
    <div className="space-y-4" data-testid="donors-list">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 font-['Chivo']">Donors</h2>
        <Button onClick={() => { setEditDonor(null); setShowForm(true); }} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-sm" data-testid="create-donor-btn">
          <Plus size={16} className="mr-1" /> New Donor
        </Button>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-64">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search donors..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 rounded-sm border-slate-300" data-testid="donor-search-input" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-36 rounded-sm"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="individual">Individual</SelectItem><SelectItem value="organization">Organization</SelectItem><SelectItem value="church">Church</SelectItem><SelectItem value="foundation">Foundation</SelectItem><SelectItem value="corporate">Corporate</SelectItem></SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 rounded-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="lapsed">Lapsed</SelectItem></SelectContent>
        </Select>
        {categories.length > 0 && (
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40 rounded-sm"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Categories</SelectItem>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Strip */}
      <div className="flex items-center gap-6 text-sm text-slate-500">
        <span><span className="font-semibold text-slate-700">{donors.length}</span> donors</span>
        <span>Total: <span className="currency font-semibold text-slate-700">{fmtCurrency(donors.reduce((s, d) => s + (d.total_donated || 0), 0))}</span></span>
        <span>Active: <span className="font-semibold text-emerald-600">{donors.filter(d => (d.status || 'active') === 'active').length}</span></span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {[
                { key: 'name', label: 'Name' },
                { key: 'type', label: 'Type' },
                { key: 'status', label: 'Status' },
                { key: 'category', label: 'Category' },
                { key: 'total_donated', label: 'Total Donated' },
                { key: 'donation_count', label: 'Donations' },
                { key: 'last_donation_date', label: 'Last Donation' },
                { key: 'paired_students', label: 'Students' },
              ].map(col => (
                <th key={col.key} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4 cursor-pointer select-none hover:text-slate-800"
                  onClick={() => toggleSort(col.key)}>
                  <span className="flex items-center gap-1">{col.label}
                    {sortField === col.key && <ArrowsDownUp size={10} className="text-[#0F6CBD]" />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-8 text-center text-sm text-slate-400">Loading...</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={8} className="py-8 text-center text-sm text-slate-400">No donors found</td></tr>
            ) : sorted.map(d => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => selectDonor(d.id)} data-testid={`donor-row-${d.id}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-[#0F6CBD]/10 rounded-sm flex items-center justify-center text-[#0F6CBD] font-semibold text-xs shrink-0">
                      {d.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-slate-800 block">{d.name}</span>
                      {d.company && <span className="text-xs text-slate-400">{d.company}</span>}
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4"><Badge variant="outline" className="capitalize text-xs">{d.type}</Badge></td>
                <td className="py-3 px-4">
                  <Badge className={`capitalize text-xs border ${STATUS_STYLES[d.status || 'active']}`}>{d.status || 'active'}</Badge>
                </td>
                <td className="py-3 px-4 text-sm text-slate-600">{d.category || '-'}</td>
                <td className="py-3 px-4 currency text-sm font-medium text-slate-800">{fmtCurrency(d.total_donated)}</td>
                <td className="py-3 px-4 text-sm text-slate-600 text-center">{d.donation_count || 0}</td>
                <td className="py-3 px-4 text-sm text-slate-500">{d.last_donation_date || '-'}</td>
                <td className="py-3 px-4 text-sm text-slate-600 text-center">{d.paired_students?.length || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && <DonorForm />}
    </div>
  );
}
